/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '~/components/Modal'
import { useServiceContext } from '~/root'
import { useEsignature } from '~/hooks/useEsignature'
import { getMasterDataInstances, updateMasterDataPropertiesOnInstances } from '~/services/api/etmf'
import { MasterDataType } from '~/constants/etmf'
import LoadingSpinner from '~/components/LoadingSpinner'

export type MasterDataTypeValue = (typeof MasterDataType)[keyof typeof MasterDataType]

interface UpdateTrialsModalProps {
  fsiId: number
  type: MasterDataTypeValue
  onClose?: () => void
}

interface MasterDataInstance {
  fsiId: number
  path: string
  [key: string]: any
}

interface UpdateRequest {
  masterDatafsiId: number
  instantiatedFsiIds: number[]
}

const UpdateTrialsModal: React.FC<UpdateTrialsModalProps> = ({ fsiId, type, onClose }) => {
  const { t } = useTranslation()
  const context = useServiceContext()
  const { signAction } = useEsignature()

  const [masterDataInstances, setMasterDataInstances] = useState<MasterDataInstance[]>([])
  const [selectedAllEntities, setSelectedAllEntities] = useState(false)
  const [noInstances, setNoInstances] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [request, setRequest] = useState<UpdateRequest>({
    masterDatafsiId: fsiId,
    instantiatedFsiIds: []
  })

  // Get modal title based on type
  const getTitle = () => {
    switch (type) {
      case MasterDataType.TRIAL:
        return t('ETMF_UPDATE_TRIALS_TITLE_UPDATE_STUDIES')
      case MasterDataType.SITE:
        return t('ETMF_UPDATE_TRIALS_TITLE_UPDATE_SITES')
      case MasterDataType.COUNTRY:
        return t('ETMF_UPDATE_TRIALS_TITLE_UPDATE_COUNTRIES')
      case MasterDataType.PRODUCT:
        return t('ETMF_UPDATE_TRIALS_TITLE_UPDATE_PRODUCTS')
      case MasterDataType.ENVIRONMENT:
        return t('ETMF_UPDATE_TRIALS_TITLE_UPDATE_ENVIRONMENTS')
      default:
        return t('ETMF_UPDATE_TRIALS_TITLE_UPDATE_STUDIES')
    }
  }

  // Get label text based on type
  const getLabel = () => {
    switch (type) {
      case MasterDataType.TRIAL:
        return t('ETMF_UPDATE_TRIALS_LABEL_SELECT_STUDIES')
      case MasterDataType.SITE:
        return t('ETMF_UPDATE_TRIALS_LABEL_SELECT_SITES')
      case MasterDataType.COUNTRY:
        return t('ETMF_UPDATE_TRIALS_LABEL_SELECT_COUNTRIES')
      case MasterDataType.PRODUCT:
        return t('ETMF_UPDATE_TRIALS_LABEL_SELECT_PRODUCTS')
      case MasterDataType.ENVIRONMENT:
        return t('ETMF_UPDATE_TRIALS_LABEL_SELECT_ENVIRONMENTS')
      default:
        return t('ETMF_UPDATE_TRIALS_LABEL_SELECT_STUDIES')
    }
  }

  // Get "no associated items" message based on type
  const getNoAssociatedItemsMsg = () => {
    switch (type) {
      case MasterDataType.TRIAL:
        return t('ETMF_UPDATE_TRIALS_NO_ASSOC_ITEMS')
      case MasterDataType.SITE:
        return t('ETMF_UPDATE_SITES_NO_ASSOC_ITEMS')
      case MasterDataType.COUNTRY:
        return t('ETMF_UPDATE_COUNTRIES_NO_ASSOC_ITEMS')
      case MasterDataType.PRODUCT:
        return t('ETMF_UPDATE_PRODUCTS_NO_ASSOC_ITEMS')
      case MasterDataType.ENVIRONMENT:
        return t('ETMF_UPDATE_ENVS_NO_ASSOC_ITEMS')
      default:
        return t('ETMF_UPDATE_TRIALS_NO_ASSOC_ITEMS')
    }
  }

  // Initialize - fetch master data instances
  const init = useCallback(async () => {
    try {
      setLoading(true)
      const instances = await getMasterDataInstances(fsiId)

      if (!instances || instances.length === 0) {
        setNoInstances(true)
        setMasterDataInstances([])
      } else {
        setNoInstances(false)
        setMasterDataInstances(instances)
      }
    } catch (error) {
      console.error('Error loading master data instances:', error)
      setNoInstances(true)
    } finally {
      setLoading(false)
    }
  }, [fsiId])

  useEffect(() => {
    init()
  }, [init])

  // Toggle individual entity selection
  const selectedCountry = (fsiId: number) => {
    setSelectedAllEntities(false)
    setRequest((prev) => {
      const isSelected = prev.instantiatedFsiIds.includes(fsiId)

      if (isSelected) {
        return {
          ...prev,
          instantiatedFsiIds: prev.instantiatedFsiIds.filter((id) => id !== fsiId)
        }
      } else {
        return {
          ...prev,
          instantiatedFsiIds: [...prev.instantiatedFsiIds, fsiId]
        }
      }
    })
  }

  // Toggle select all
  const selectAllEntities = () => {
    const newSelectAll = !selectedAllEntities
    setSelectedAllEntities(newSelectAll)

    if (newSelectAll) {
      setRequest((prev) => ({
        ...prev,
        instantiatedFsiIds: masterDataInstances.map((instance) => instance.fsiId)
      }))
    } else {
      setRequest((prev) => ({
        ...prev,
        instantiatedFsiIds: []
      }))
    }
  }

  // Save with e-signature
  const save = async () => {
    if (request.instantiatedFsiIds.length === 0) {
      return
    }

    // Close modal immediately (like AngularJS version)
    handleClose()

    // Check if e-signature is required
    const requiresEsig = context.service.auth.requireESignatureForFileCommands()

    if (requiresEsig) {
      try {
        const esignature = await signAction(
          'ADMIN_ACTION_ETMF_UPDATE_MD_PROPERTIES',
          'ADMIN_ACTION_ETMF_UPDATE_MD_PROPERTIES'
        )
        await doUpdate(esignature)
      } catch (error) {
        // User cancelled e-signature
        console.log('E-signature cancelled')
      }
    } else {
      await doUpdate()
    }
  }

  // Perform the update
  const doUpdate = async (esignature?: Record<string, unknown> | any) => {
    try {
      setSaving(true)

      await updateMasterDataPropertiesOnInstances(request, esignature)

      context.service.toast.success(t('ETMF_UPDATE_TRIALS_MSG_SUCCESS'))

      // Notify parent or global listeners using window event bus
      window.dispatchEvent(new CustomEvent('ETMF_UPDATE_TRIALS_SUCCESS'))

      handleClose()
    } catch (error: any) {
      console.error('Error updating master data:', error)
      const errorMsg = error?.message || t('ERROR_SAVING_DATA')
      context.service.toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  // Truncate path for display (show first 80 chars with ...)
  const excerptFilter = (path: string, maxLength: number = 80) => {
    if (path && path.length > maxLength) {
      return path.substring(0, maxLength) + '...'
    }
    return path
  }

  return (
    <Modal show={true} handleClose={handleClose} title={getTitle()} size="lg" className="generateClinicalStructure">
      <div className="modal-body">
        {loading ? (
          <LoadingSpinner centered size={40} />
        ) : (
          <>
            <div className="form-group md-form-group w100p mb0">
              <label className="fs12-imp">{getLabel()}</label>
              <div className="table-responsive">
                <table className="styled-table chita-table generator-table-master-data mb10">
                  <thead>
                    <tr className="settings">
                      <th>
                        <div className="form-group checkbox-form-group mb0 mt5">
                          <input
                            type="checkbox"
                            id="input-selectAll"
                            name="selectAll"
                            checked={selectedAllEntities}
                            onChange={selectAllEntities}
                          />
                          <label htmlFor="input-selectAll">
                            <div className="chkbc"></div>
                          </label>
                        </div>
                        <span className="font-bold cursor-pointer">{t('NAME')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {masterDataInstances.map((file) => (
                      <tr key={file.fsiId}>
                        <td title={file.path}>
                          <div className="form-group checkbox-form-group mb0">
                            <input
                              type="checkbox"
                              id={`countryChecked${file.fsiId}`}
                              checked={request.instantiatedFsiIds.includes(file.fsiId)}
                              onChange={() => selectedCountry(file.fsiId)}
                            />
                            <label htmlFor={`countryChecked${file.fsiId}`}>
                              <div className="chkbc"></div>
                            </label>
                          </div>
                          {excerptFilter(file.path)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {noInstances && <div className="pl40 fs12">{getNoAssociatedItemsMsg()}</div>}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="modal-footer">
        <button className="btn btn-link" type="button" onClick={handleClose} disabled={saving}>
          {t('CANCEL')}
        </button>
        <button
          className={`btn btn-primary at_btnAddFolder ${request.instantiatedFsiIds.length === 0 ? 'text-grey' : 'text-blue'}`}
          type="button"
          onClick={save}
          disabled={saving || request.instantiatedFsiIds.length === 0}
        >
          {saving ? (
            <>
              <LoadingSpinner size={16} />
              {' ' + t('SAVING')}
            </>
          ) : (
            t('ETMF_UPDATE_TRIALS_BTN_UPDATE')
          )}
        </button>
      </div>
    </Modal>
  )
}

export default UpdateTrialsModal
